'use client';

import { c as _c } from "react/compiler-runtime";
import { buildAvatarUrl } from '@shared/all/helpers/avatarUrl';
import { isUserOnline } from '@shared/all/helpers/onlineStatus';
import { type AvatarCorner, getAvatarBadgeGeometry, getAvatarCornerOffsets } from '@shared/utils/helpers/avatarBadge';
import { API_URL } from '@shared/utils/helpers/config';
import { FACTIII_BRAND_INDIGO, getFactiiiColor } from '@shared/utils/helpers/getFactiiiColor';
import { getFactiiiInitials } from '@shared/utils/helpers/getFactiiiInitials';
import { getReadableTextColor } from '@shared/utils/helpers/getReadableTextColor';
import { getUserInitials } from '@shared/utils/helpers/getUserInitials';
import { useConversations } from '@shared/utils/hooks/useConversations';
import { useFactiiisCache } from '@shared/utils/hooks/useFactiiis';
import useUser from '@shared/utils/hooks/useUser';
import { AnonymousAvatar, BotIcon, DefaultUserAvatar, Logo } from '@shared/utils/icons';
import { type FactiiiAvatar, type SpaceAvatar, type UserAvatar } from '@shared/utils/types/avatar';
import { Check, Gem, Loader2, Lock } from 'lucide-react';
import Image, { type StaticImageData } from 'next/image';
import { type ReactNode, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import useEffectiveTheme from '../../hooks/useEffectiveTheme';
import { getS3ObjectURL } from '../../utils/s3';
import { BotExplanationModal } from './BotExplanationModal';

// Extend shared types for client-specific needs
type ClientUserAvatar = Omit<UserAvatar, 'avatarKey'> & {
  avatarKey: StaticImageData | string | null;
};
type Props = (ClientUserAvatar | SpaceAvatar | FactiiiAvatar) & {
  size?: number;
  className?: string;
  isRetired?: boolean;
  pin?: boolean;
  isApproximate?: boolean;
  // When true, render a spinner glyph instead of the default fallback
  // image/Logo. Used by map pins while the factiii batch query is loading.
  isLoading?: boolean;
  /**
   * Draw a separator ring around the avatar — what overlapping stacks
   * (`-space-x-2`) need so neighbours don't blur together.
   *
   * Use this instead of passing `ring-2 ring-background-secondary` (or worse, a
   * `rounded-full` wrapper) via className: the ring is applied to the same
   * element that owns the shape, so it follows the circle for users/spaces and
   * the 20%-rounded square for factiiis automatically. A hand-rolled
   * `rounded-full` ring around a factiii avatar traces a circle over a rounded
   * square, which is where the broken-looking borders came from.
   */
  border?: boolean;
  /**
   * A control drawn on the avatar's rim — the profile editor's camera button is
   * the only one today. Placed with the same 45° geometry the badges use, so it
   * lands half over the circle instead of being nudged there by hand.
   *
   * Rendering one hides every status badge. They share the rim, the control is
   * larger than any of them, and an editing affordance the user is aiming at
   * should not have an online dot underneath it. The status is not information
   * the owner needs while changing their own picture.
   */
  action?: {
    node: ReactNode;
    /** The control's own diameter. Its centre lands on the circle regardless. */
    size: number;
    corner?: AvatarCorner;
  };
};

/** The tile a space falls back to: no uploaded picture, or one that will not draw. */
const SPACE_DEFAULT_AVATAR = '/images/space-avatar-default.png';
const Avatar = t0 => {
  const $ = _c(41);
  const {
    size: t1,
    className: t2,
    isRetired: t3,
    pin: t4,
    isApproximate: t5,
    isLoading: t6,
    border: t7,
    action,
    ...props
  } = t0;
  const size = t1 === undefined ? 35 : t1;
  const className = t2 === undefined ? "" : t2;
  const isRetired = t3 === undefined ? false : t3;
  const pin = t4 === undefined ? false : t4;
  const isApproximate = t5 === undefined ? false : t5;
  const isLoading = t6 === undefined ? false : t6;
  const border = t7 === undefined ? false : t7;
  const {
    currentUser
  } = useUser();
  const [showBotModal, setShowBotModal] = useState(false);
  const [failedSrc, setFailedSrc] = useState(null);
  const {
    conversationsState
  } = useConversations();
  const factiiis = useFactiiisCache();
  const theme = useEffectiveTheme();
  const isUserAvatar = "avatarKey" in props && "robohash" in props;
  const userId = isUserAvatar ? (props as ClientUserAvatar).userId : undefined;
  const participant = conversationsState.conversations.flatMap(_temp).find(p => p.id === userId);
  const isOnline = currentUser?.id === userId ? false : participant ? isUserOnline(participant.isActive, participant.lastActive) : false;
  const isSpaceAvatar = "spaceAvatarKey" in props;
  const isFactiiiAvatar = "factiiiAvatarKey" in props;
  const isBotUser = isUserAvatar && (props as ClientUserAvatar).isBot;
  const isCurrentUserPrivate = isUserAvatar && currentUser?.id === userId && currentUser?.isPrivate;
  const isVerified = isUserAvatar && (props as ClientUserAvatar).tag === "VERIFIED";
  const isFounder = isUserAvatar && (props as ClientUserAvatar).isFounder;
  const getAvatarSrc = () => {
    if (isUserAvatar) {
      const {
        avatarKey,
        robohash
      } = props as ClientUserAvatar;
      if (avatarKey && avatarKey !== "/images/display-image-default.png") {
        return {
          src: typeof avatarKey === "string" ? getS3ObjectURL(avatarKey) : avatarKey
        };
      }
      if (robohash) {
        return {
          src: buildAvatarUrl(API_URL, robohash),
          generated: true
        };
      }
      return {
        src: null
      };
    }
    if (isSpaceAvatar) {
      const {
        spaceAvatarKey
      } = props as SpaceAvatar;
      if (spaceAvatarKey) {
        return {
          src: getS3ObjectURL(spaceAvatarKey)
        };
      }
      return {
        src: SPACE_DEFAULT_AVATAR
      };
    }
    if (isFactiiiAvatar) {
      const {
        factiiiAvatarKey
      } = props as FactiiiAvatar;
      if (factiiiAvatarKey) {
        return {
          src: getS3ObjectURL(factiiiAvatarKey)
        };
      }
      return {
        src: null
      };
    }
    return {
      src: null
    };
  };
  const requested = getAvatarSrc();
  const imageFailed = typeof requested.src === "string" && failedSrc === requested.src;
  const avatarSrc = imageFailed && isSpaceAvatar ? SPACE_DEFAULT_AVATAR : requested.src;
  const isGeneratedAvatar = !imageFailed && requested.generated;
  const isLocalUrl = typeof avatarSrc === "string" && avatarSrc.startsWith("blob:");
  const useLogoComponent = isFactiiiAvatar && (!(props as FactiiiAvatar).factiiiAvatarKey || imageFailed);
  const isAnonymousUser = isUserAvatar && (props as ClientUserAvatar).isAnonymous === true;
  const userAvatarKey = isUserAvatar ? (props as ClientUserAvatar).avatarKey : null;
  const hasRealAvatar = userAvatarKey && userAvatarKey !== "/images/display-image-default.png";
  const useDefaultUserAvatar = !isAnonymousUser && isUserAvatar && (!hasRealAvatar && !(props as ClientUserAvatar).robohash || imageFailed);
  const factiiiName = (props as FactiiiAvatar).factiiiName ?? factiiis.find(factiii => factiii.id === (props as FactiiiAvatar).factiiiId)?.name;
  const factiiiInitials = useLogoComponent ? getFactiiiInitials(factiiiName) : null;
  const userInitials = isUserAvatar ? getUserInitials((props as ClientUserAvatar).name, (props as ClientUserAvatar).username) : null;
  const showUserMonogram = isUserAvatar && !isAnonymousUser && (imageFailed || useDefaultUserAvatar) && !!userInitials;
  const getAltText = () => {
    if (isUserAvatar) {
      const {
        name,
        username
      } = props as ClientUserAvatar;
      const who = name?.trim() || (username ? `@${username}` : "");
      if (who && !isAnonymousUser) {
        return `${who} avatar`;
      }
      return isBotUser ? "Bot avatar" : "User avatar";
    }
    if (isSpaceAvatar) {
      return "Space avatar";
    }
    if (isFactiiiAvatar) {
      return factiiiName ? `${factiiiName} topic` : "Factiii avatar";
    }
    return "Avatar";
  };
  let t8;
  if ($[0] !== setShowBotModal) {
    t8 = e => {
      e.preventDefault();
      e.stopPropagation();
      setShowBotModal(true);
    };
    $[0] = setShowBotModal;
    $[1] = t8;
  } else {
    t8 = $[1];
  }
  const handleBotBadgeClick = t8;
  const {
    badgeSize,
    iconSize
  } = getAvatarBadgeGeometry(size);
  const accentColor = "factiiiId" in props && props.factiiiId ? getFactiiiColor(props.factiiiId, theme) : FACTIII_BRAND_INDIGO;
  const triangleHeight = Math.round(size * 0.5);
  const triangleWidth = Math.round(size * 0.8);
  let t9;
  if ($[2] !== isFactiiiAvatar || $[3] !== size) {
    t9 = isFactiiiAvatar ? Math.round(size * 0.03) : Math.round(size * 0.2);
    $[2] = isFactiiiAvatar;
    $[3] = size;
    $[4] = t9;
  } else {
    t9 = $[4];
  }
  const triangleOverlap = t9;
  const triangleSvgHeight = triangleHeight + triangleOverlap;
  const factiiiTileColor = isFactiiiAvatar ? accentColor : undefined;
  const factiiiTextColor = factiiiTileColor ? getReadableTextColor(factiiiTileColor) : undefined;
  const initialsFontSize = Math.round(size * ((factiiiInitials?.length ?? 1) > 1 ? 0.52 : 0.66));
  const factiiiFallback = factiiiInitials ? <span data-testid="avatar-factiii-initials" className="flex select-none items-center justify-center font-custom font-semibold leading-none tracking-tight" style={{
    width: size,
    height: size,
    backgroundColor: factiiiTileColor,
    color: factiiiTextColor,
    fontSize: initialsFontSize
  }}>{factiiiInitials}</span> : <Logo height={size} bgColor={factiiiTileColor} color={factiiiTextColor} />;
  let t10;
  if ($[5] !== size) {
    t10 = Math.round(size * 0.6);
    $[5] = size;
    $[6] = t10;
  } else {
    t10 = $[6];
  }
  const pinLoader = <span data-testid="avatar-pin-loader" className="flex items-center justify-center" style={{
    width: size,
    height: size,
    backgroundColor: accentColor
  }}><Loader2 size={t10} className="animate-spin" style={{
      color: getReadableTextColor(accentColor)
    }} /></span>;
  const t11 = "relative inline-block";
  let t12;
  if ($[7] !== pin || $[8] !== size || $[9] !== triangleHeight) {
    t12 = pin ? {
      height: size + triangleHeight,
      width: size
    } : undefined;
    $[7] = pin;
    $[8] = size;
    $[9] = triangleHeight;
    $[10] = t12;
  } else {
    t12 = $[10];
  }
  const t13 = pin && <svg data-testid="avatar-pin-triangle" width={triangleWidth} height={triangleSvgHeight} viewBox={`0 0 ${triangleWidth} ${triangleSvgHeight}`} xmlns="http://www.w3.org/2000/svg" style={{
    position: "absolute",
    left: "50%",
    bottom: 0,
    transform: "translateX(-50%)",
    display: "block"
  }}><polygon points={`0,0 ${triangleWidth},0 ${triangleWidth / 2},${triangleSvgHeight}`} fill={accentColor} /></svg>;
  const t14 = "relative";
  let t15;
  if ($[11] !== size) {
    t15 = {
      width: size,
      height: size
    };
    $[11] = size;
    $[12] = t15;
  } else {
    t15 = $[12];
  }
  const t16 = `${className} ${isRetired ? "grayscale" : ""} ${border ? "ring-2 ring-background-secondary" : ""} flex items-center justify-center`;
  const t17 = isFactiiiAvatar ? size * 0.2 : "9999px";
  const t18 = pin && isApproximate ? `2px dashed ${accentColor}` : undefined;
  let t19;
  if ($[13] !== size || $[14] !== t17 || $[15] !== t18) {
    t19 = {
      width: size,
      height: size,
      borderRadius: t17,
      border: t18,
      overflow: "hidden",
      boxSizing: "border-box"
    };
    $[13] = size;
    $[14] = t17;
    $[15] = t18;
    $[16] = t19;
  } else {
    t19 = $[16];
  }
  const t20 = pin && isLoading ? pinLoader : useLogoComponent ? factiiiFallback : isAnonymousUser ? <AnonymousAvatar size={size} /> : showUserMonogram ? <div className="flex h-full w-full items-center justify-center rounded-full" style={{
    backgroundColor: FACTIII_BRAND_INDIGO,
    color: getReadableTextColor(FACTIII_BRAND_INDIGO),
    fontSize: size * 0.4,
    fontWeight: 600,
    lineHeight: 1
  }} aria-label={getAltText()}>{userInitials}</div> : useDefaultUserAvatar ? <DefaultUserAvatar size={size} /> : isGeneratedAvatar ? <img src={avatarSrc as string} alt={getAltText()} width={size} height={size} decoding="async" onError={imageFailed ? undefined : () => setFailedSrc(avatarSrc as string)} className={`${isRetired ? "grayscale" : ""} rounded-full`} /> : <Image src={avatarSrc} alt={getAltText()} width={size} height={size} unoptimized={isLocalUrl} onError={imageFailed ? undefined : () => setFailedSrc(avatarSrc as string)} className={`${isRetired ? "grayscale" : ""} ${isFactiiiAvatar ? "rounded-[20%]" : "rounded-full"}`} />;
  let t21;
  if ($[17] !== t16 || $[18] !== t19 || $[19] !== t20) {
    t21 = <div className={t16} style={t19}>{t20}</div>;
    $[17] = t16;
    $[18] = t19;
    $[19] = t20;
    $[20] = t21;
  } else {
    t21 = $[20];
  }
  const t22 = !action && isBotUser && <span role="button" onClick={handleBotBadgeClick} className="group absolute z-20 flex cursor-pointer items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-purple-600 shadow-lg transition-all duration-200 hover:scale-110 hover:shadow-xl active:scale-95" style={{
    width: badgeSize,
    height: badgeSize,
    ...getAvatarCornerOffsets(size, badgeSize, "bottom-left")
  }} title="Bot Account - Click to learn more" aria-label="Bot account badge - Click to learn more about bot accounts"><div className="relative z-10 transition-transform group-hover:rotate-12"><BotIcon size={iconSize} /></div></span>;
  const t23 = !action && isOnline && <div className="absolute z-20 rounded-full bg-green-500" style={{
    width: badgeSize,
    height: badgeSize,
    ...getAvatarCornerOffsets(size, badgeSize, "bottom-right")
  }} title="Online" aria-label="User is online" />;
  const t24 = !action && (() => {
    if (isVerified) {
      return <span className="absolute z-20 flex items-center justify-center rounded-full bg-emerald-600 shadow" style={{
        width: badgeSize,
        height: badgeSize,
        ...getAvatarCornerOffsets(size, badgeSize, "top-right")
      }} aria-label="Verified account" title="Verified"><Check size={iconSize} className="text-white" /></span>;
    }
    if (isFounder) {
      return <span className="absolute z-20 flex items-center justify-center rounded-full bg-amber-500 shadow" style={{
        width: badgeSize,
        height: badgeSize,
        ...getAvatarCornerOffsets(size, badgeSize, "top-right")
      }} aria-label="Founder account" title="Founder"><Gem size={iconSize} className="text-white" /></span>;
    }
    if (isCurrentUserPrivate) {
      return <PrivateBadge badgeSize={badgeSize} iconSize={iconSize} avatarSize={size} />;
    }
    return null;
  })();
  let t25;
  if ($[21] !== action || $[22] !== size) {
    t25 = action && <div className="absolute z-20" style={{
      width: action.size,
      height: action.size,
      ...getAvatarCornerOffsets(size, action.size, action.corner ?? "bottom-right")
    }}>{action.node}</div>;
    $[21] = action;
    $[22] = size;
    $[23] = t25;
  } else {
    t25 = $[23];
  }
  let t26;
  if ($[24] !== t15 || $[25] !== t21 || $[26] !== t22 || $[27] !== t23 || $[28] !== t24 || $[29] !== t25) {
    t26 = <div className={t14} style={t15}>{t21}{t22}{t23}{t24}{t25}</div>;
    $[24] = t15;
    $[25] = t21;
    $[26] = t22;
    $[27] = t23;
    $[28] = t24;
    $[29] = t25;
    $[30] = t26;
  } else {
    t26 = $[30];
  }
  let t27;
  if ($[31] !== t12 || $[32] !== t13 || $[33] !== t26) {
    t27 = <div className={t11} style={t12}>{t13}{t26}</div>;
    $[31] = t12;
    $[32] = t13;
    $[33] = t26;
    $[34] = t27;
  } else {
    t27 = $[34];
  }
  let t28;
  if ($[35] !== setShowBotModal || $[36] !== showBotModal) {
    t28 = showBotModal && <BotExplanationModal closeHandler={() => setShowBotModal(false)} />;
    $[35] = setShowBotModal;
    $[36] = showBotModal;
    $[37] = t28;
  } else {
    t28 = $[37];
  }
  let t29;
  if ($[38] !== t27 || $[39] !== t28) {
    t29 = <>{t27}{t28}</>;
    $[38] = t27;
    $[39] = t28;
    $[40] = t29;
  } else {
    t29 = $[40];
  }
  return t29;
};
export default Avatar;
const TOOLTIP_WIDTH = 240; // matches w-60
const TOOLTIP_GAP = 8;

/**
 * Private-account hover tooltip.
 *
 * Renders the tooltip in a portal so ancestor `overflow: hidden` (sidebar
 * rows, chat items, modal cards) can't clip it. Anchor strategy:
 *   - "above" placement uses CSS `bottom` so the tooltip's bottom edge sits
 *     `GAP` px above the badge's top — the browser picks the top from the
 *     rendered height, so we don't need to know the height in advance.
 *     (The earlier `top: rect.top - estimatedHeight` approach left a visible
 *     ~40px gap whenever the estimate didn't match the real height.)
 *   - "below" placement uses CSS `top` directly from the badge's bottom,
 *     also height-independent.
 * Auto-flips to below when there isn't enough room above.
 */
type TooltipPos = {
  mode: 'above';
  bottom: number;
  left: number;
} | {
  mode: 'below';
  top: number;
  left: number;
};
const PrivateBadge = t0 => {
  const $ = _c(18);
  const {
    badgeSize,
    iconSize,
    avatarSize
  } = t0;
  const badgeRef = useRef(null);
  const [pos, setPos] = useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (!badgeRef.current) {
        return;
      }
      const rect = badgeRef.current.getBoundingClientRect();
      let left = rect.right - TOOLTIP_WIDTH;
      left = Math.max(TOOLTIP_GAP, Math.min(left, window.innerWidth - TOOLTIP_WIDTH - TOOLTIP_GAP));
      const spaceAbove = rect.top;
      if (spaceAbove >= 200) {
        setPos({
          mode: "above",
          bottom: window.innerHeight - rect.top + TOOLTIP_GAP,
          left
        });
      } else {
        setPos({
          mode: "below",
          top: rect.bottom + TOOLTIP_GAP,
          left
        });
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const showTooltip = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setPos(null);
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const hideTooltip = t2;
  let t3;
  if ($[2] !== avatarSize || $[3] !== badgeSize) {
    t3 = getAvatarCornerOffsets(avatarSize, badgeSize, "top-right");
    $[2] = avatarSize;
    $[3] = badgeSize;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== badgeSize || $[6] !== t3) {
    t4 = {
      width: badgeSize,
      height: badgeSize,
      ...t3
    };
    $[5] = badgeSize;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== iconSize) {
    t5 = <Lock size={iconSize} className="text-white" />;
    $[8] = iconSize;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t4 || $[11] !== t5) {
    t6 = <div ref={badgeRef} onMouseEnter={showTooltip} onMouseLeave={hideTooltip} onFocus={showTooltip} onBlur={hideTooltip} tabIndex={0} role="button" className="absolute z-20 flex cursor-pointer items-center justify-center rounded-full bg-slate-700 shadow" style={t4} aria-label="Private account">{t5}</div>;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== pos) {
    t7 = pos && typeof document !== "undefined" && createPortal(<div role="tooltip" className="pointer-events-none fixed z-[9999] w-60 rounded-md border border-ds-border-standard bg-background-secondary p-2 text-left text-xs text-textPrimary shadow-lg" style={pos.mode === "above" ? {
      bottom: pos.bottom,
      left: pos.left
    } : {
      top: pos.top,
      left: pos.left
    }}><div className="font-semibold text-textPrimary">Private account</div><div className="mt-0.5 text-textSecondary">Your profile and content don't appear in public search, the map, or community discovery. Only you can see them unless you change this in settings.</div></div>, document.body);
    $[13] = pos;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t6 || $[16] !== t7) {
    t8 = <>{t6}{t7}</>;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
};
function _temp(c) {
  return c.participants || [];
}